/**
 * Performance Monitoring Dashboard
 * Real-time algorithm performance monitoring
 */

import { useState, useEffect } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '../trading/card'
import { MetricCard } from '../trading/metric-card'
import { TradingLineChart, TradingBarChart } from '../trading/chart'
import { Badge, StatusBadge } from '../trading/badge'
import { Button } from '../ui/button'
import { Tabs, TabsPanel, TabPanel } from '../trading/tabs'
import { Play, Pause, AlertTriangle, CheckCircle, Clock, TrendingUp, TrendingDown, Activity } from 'lucide-react'
import { formatNumber, formatPercent } from '@/lib/utils'

interface PerformanceDashboardProps {
  algorithm: any
}

export function PerformanceDashboard({ algorithm }: PerformanceDashboardProps) {
  const [activeTab, setActiveTab] = useState('overview')
  const [liveData, setLiveData] = useState({
    isRunning: algorithm.status === 'active',
    currentBalance: algorithm.performance?.totalReturn || 10000,
    dailyPnL: 0,
    openPositions: 3,
    todayTrades: 7,
    winRate: 65,
    drawdown: 5.2,
  })

  // Simulate real-time updates
  useEffect(() => {
    if (!liveData.isRunning) return

    const interval = setInterval(() => {
      setLiveData((prev) => ({
        ...prev,
        currentBalance: prev.currentBalance + (Math.random() - 0.48) * 10,
        dailyPnL: ((prev.currentBalance - 10000) / 10000) * 100,
      }))
    }, 2000)

    return () => clearInterval(interval)
  }, [liveData.isRunning])

  const toggleTrading = () => {
    setLiveData((prev) => ({ ...prev, isRunning: !prev.isRunning }))
  }

  // Generate simulated equity curve data
  const generateEquityCurve = () => {
    const data = []
    let value = 10000
    const now = new Date()
    for (let i = 24; i >= 0; i--) {
      const time = new Date(now.getTime() - i * 60 * 60 * 1000)
      value = value + (Math.random() - 0.45) * 50
      data.push({
        time: time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        value,
        benchmark: 10000 + (24 - i) * 2,
      })
    }
    return data
  }

  const equityCurve = generateEquityCurve()

  // Simulated trade statistics
  const tradeStats = [
    { label: 'Today', value: 7, win: 5, loss: 2 },
    { label: 'This Week', value: 23, win: 15, loss: 8 },
    { label: 'This Month', value: 89, win: 58, loss: 31 },
  ]

  // Open positions data
  const openPositions = [
    { id: '1', symbol: 'EUR/USD', type: 'long', entry: 1.0850, current: 1.0875, pnl: 25, pnlPercent: 0.23 },
    { id: '2', symbol: 'GBP/USD', type: 'short', entry: 1.2630, current: 1.2615, pnl: 15, pnlPercent: 0.12 },
    { id: '3', symbol: 'BTC/USD', type: 'long', entry: 43250, current: 43400, pnl: 150, pnlPercent: 0.35 },
  ]

  // Recent activity
  const recentActivity = [
    { time: '2 min ago', type: 'trade', message: 'Closed EUR/USD long at 1.0875 (+$25)', status: 'success' },
    { time: '15 min ago', type: 'signal', message: 'Entry signal generated for GBP/USD', status: 'info' },
    { time: '32 min ago', type: 'trade', message: 'Opened BTC/USD long at $43,250', status: 'info' },
    { time: '1 hour ago', type: 'alert', message: 'Drawdown warning: 5.2%', status: 'warning' },
    { time: '2 hours ago', type: 'trade', message: 'Closed XAU/USD short at $2,040 (+$85)', status: 'success' },
  ]

  return (
    <div className="space-y-6">
      {/* Header with live status */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className={`h-3 w-3 rounded-full ${liveData.isRunning ? 'bg-success animate-pulse' : 'bg-muted'}`} />
                <span className="text-sm font-medium">
                  {liveData.isRunning ? 'Live Trading' : 'Stopped'}
                </span>
              </div>
              <Badge variant={liveData.isRunning ? 'success' : 'default'}>
                {algorithm.name}
              </Badge>
            </div>
            <Button
              onClick={toggleTrading}
              variant={liveData.isRunning ? 'destructive' : 'default'}
              size="sm"
            >
              {liveData.isRunning ? (
                <>
                  <Pause className="h-4 w-4 mr-2" />
                  Stop Trading
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Start Trading
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Current Balance"
          value={`$${formatNumber(liveData.currentBalance)}`}
          change={liveData.dailyPnL}
          changeType="percent"
          icon={<Activity className="h-6 w-6" />}
          trend={liveData.dailyPnL >= 0 ? 'up' : 'down'}
        />
        <MetricCard
          title="Daily P&L"
          value={`${liveData.dailyPnL >= 0 ? '+' : ''}${formatPercent(liveData.dailyPnL)}`}
          icon={<TrendingUp className="h-6 w-6" />}
          trend={liveData.dailyPnL >= 0 ? 'up' : 'down'}
          variant={liveData.dailyPnL >= 0 ? 'success' : 'danger'}
        />
        <MetricCard
          title="Open Positions"
          value={liveData.openPositions}
          icon={<Activity className="h-6 w-6" />}
        />
        <MetricCard
          title="Today's Trades"
          value={liveData.todayTrades}
          icon={<Clock className="h-6 w-6" />}
        />
      </div>

      {/* Tabs */}
      <Tabs
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'positions', label: 'Positions' },
          { id: 'activity', label: 'Activity' },
        ]}
        defaultTab="overview"
        onChange={setActiveTab}
        variant="underline"
      />

      <TabsPanel>
        <TabPanel value="overview" activeTab={activeTab}>
          <div className="space-y-6">
            {/* Equity Curve */}
            <Card>
              <CardHeader>
                <CardTitle>Real-Time Equity Curve</CardTitle>
              </CardHeader>
              <CardContent>
                <TradingLineChart
                  data={equityCurve}
                  lines={[
                    { dataKey: 'value', name: 'Portfolio', color: '#0066cc', strokeWidth: 2 },
                    { dataKey: 'benchmark', name: 'Benchmark', color: '#8a94a6', strokeWidth: 1 },
                  ]}
                  xAxisKey="time"
                  height={300}
                  area={true}
                />
              </CardContent>
            </Card>

            {/* Trade Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {tradeStats.map((stat) => (
                <Card key={stat.label}>
                  <CardContent className="py-6">
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground mb-2">{stat.label}</p>
                      <p className="text-3xl font-bold mb-4">{stat.value}</p>
                      <div className="flex justify-center gap-4 text-sm">
                        <div>
                          <span className="text-success">{stat.win}W</span>
                          <span className="text-muted-foreground mx-1">/</span>
                          <span className="text-destructive">{stat.loss}L</span>
                        </div>
                        <div className="text-muted-foreground">
                          ({formatPercent((stat.win / stat.value) * 100)})
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </TabPanel>

        <TabPanel value="positions" activeTab={activeTab}>
          <Card>
            <CardHeader>
              <CardTitle>Open Positions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {openPositions.map((position) => (
                  <div key={position.id} className="p-4 rounded-lg border border-border hover:border-primary/50 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <span className="font-semibold">{position.symbol}</span>
                        <Badge variant={position.type === 'long' ? 'success' : 'danger'}>
                          {position.type.toUpperCase()}
                        </Badge>
                      </div>
                      <div className={`font-semibold ${position.pnl >= 0 ? 'text-success' : 'text-destructive'}`}>
                        {position.pnl >= 0 ? '+' : ''}${formatNumber(position.pnl)}
                        <span className="text-sm text-muted-foreground ml-2">
                          ({position.pnlPercent >= 0 ? '+' : ''}{formatPercent(position.pnlPercent)})
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Entry: </span>
                        <span className="font-medium">{formatNumber(position.entry)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Current: </span>
                        <span className="font-medium">{formatNumber(position.current)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">P&L: </span>
                        <span className={`font-medium ${position.pnl >= 0 ? 'text-success' : 'text-destructive'}`}>
                          {position.pnl >= 0 ? '+' : ''}${formatNumber(position.pnl)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value="activity" activeTab={activeTab}>
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentActivity.map((activity, index) => (
                  <div key={index} className="flex gap-3 items-start">
                    <div className="flex-shrink-0 mt-1">
                      {activity.status === 'success' && <CheckCircle className="h-4 w-4 text-success" />}
                      {activity.status === 'warning' && <AlertTriangle className="h-4 w-4 text-warning" />}
                      {activity.status === 'info' && <Clock className="h-4 w-4 text-info" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{activity.message}</span>
                        <span className="text-xs text-muted-foreground">{activity.time}</span>
                      </div>
                      <Badge variant="outline" size="sm">
                        {activity.type}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabPanel>
      </TabsPanel>
    </div>
  )
}
